import { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import { X, Minimize2, Maximize2, Send, Paperclip, Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered, Mail, Loader2, ImageIcon, AlertTriangle } from "lucide-react";
import { validateImage, fileToDataUrl } from '@/lib/email/inline-images';
import { createLinkExtension, linkEditorProps } from './link-extension';
import { createImageExtension } from './image-resize-extension';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { TemplateSelector } from './templates/TemplateSelector';
import { LinkPopover } from './LinkPopover';
import { SenderSelect } from './SenderSelect';
import { useEmailSender, EmailAttachment } from '@/hooks/useEmailSender';
import { useToast } from '@/hooks/use-toast';
import { useReconnectErrorToast } from '@/lib/email/useReconnectErrorToast';
import { useEmulationGuard } from '@/hooks/useEmulationGuard';
import { captureError } from '@/lib/sentry';
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import ToContactPicker, { type ToRecipient } from './composer/ToContactPicker';
import TemplatePopulateDialog, { type PopulateResult } from './TemplatePopulateDialog';
import { decideTemplateRoute } from './composer/template-route';
import { type ContactLike } from '@/lib/email/oneoff-merge';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { track } from '@/lib/analytics';
import { buildUnfilledWarning, buildRecipientMismatchWarning } from './composer/presend-gate';
import { resolvePreselectedRecipient } from './composer/preselect';
import { useConfirm } from './composer/useConfirm';

interface EmailComposerProps {
  defaultTo?: string;
  defaultSubject?: string;
  defaultBody?: string;
  onSave?: (subject: string, body: string) => void;
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
  defaultContactId?: string;
}

// Size limits
const GMAIL_WARNING_SIZE = 20 * 1024 * 1024; // 20MB warning
const GMAIL_MAX_SIZE = 25 * 1024 * 1024; // 25MB limit

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function EmailComposer({
  defaultTo = "",
  defaultSubject = "",
  defaultBody = "",
  onSave,
  onClose,
  onMinimize,
  isMinimized = false,
  defaultContactId,
}: EmailComposerProps) {
  const [to, setTo] = useState<ToRecipient>(
    defaultTo ? { kind: 'raw', email: defaultTo } : null,
  );
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const { currentWorkspace } = useWorkspaces();
  const [subject, setSubject] = useState(defaultSubject);
  const [characterCount, setCharacterCount] = useState(defaultSubject.length);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const composerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const { selectedProvider, selectProvider, providers, loading: providerLoading, initialized, sendEmail } = useEmailSender();
  const [sending, setSending] = useState(false);
  const [showNoContactFlag, setShowNoContactFlag] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<{ subject: string; body: string } | null>(null);
  // Which contact the body was personalized from (id + display name), so we can
  // warn if the recipient is later swapped before sending. null = body is not
  // personalized (raw insert / never populated).
  const [populatedFrom, setPopulatedFrom] = useState<{ id: string; name: string } | null>(null);
  // Amber caution shown in the composer when the recipient is changed after
  // populating; cleared on re-populate or when the recipient matches again.
  const [recipientChangedAfterPopulate, setRecipientChangedAfterPopulate] = useState<string | null>(null);
  const { toast } = useToast();
  const showReconnectErrorToast = useReconnectErrorToast();
  const { isEmulating, blockIfEmulating } = useEmulationGuard();
  const { confirm, ConfirmDialog } = useConfirm();
  // Tracks whether the user has manually changed the To field after the
  // composer opened. The contactId preselect fetch is async — it must not
  // clobber a recipient the user already edited while waiting for the fetch.
  const userTouchedTo = useRef(false);

  // Preselect a contact when opened from a contact context.
  useEffect(() => {
    let cancelled = false;
    if (!defaultContactId || !currentWorkspace?.id) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('contacts_with_primary_details')
          .select('id, first_name, last_name, company_name, job_title, primary_email, primary_phone')
          .eq('workspace_id', currentWorkspace.id)
          .eq('id', defaultContactId)
          .maybeSingle();
        // Don't clobber a recipient the user already set: only preselect if the
        // current recipient is still null (race between this async fetch and the
        // user typing/picking a recipient).
        if (!cancelled && data) {
          setTo((prev) => resolvePreselectedRecipient(prev, data as ContactLike, userTouchedTo.current));
        }
      } catch (err) {
        captureError(err as Error, { feature: 'oneoff-template-populate' });
      }
    })();
    return () => { cancelled = true; };
  }, [defaultContactId, currentWorkspace?.id]);

  // Handle image insertion from paste or drop
  const handleImageInsert = async (file: File) => {
    const validation = validateImage(file);
    if (!validation.valid) {
      toast({
        title: "Image Error",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      editor?.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to insert image",
        variant: "destructive",
      });
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      createLinkExtension(),
      TiptapUnderline,
      // Click-to-resize image (Google-Docs-style corner/edge handles). The
      // renderHTML override emits both a `width` HTML attribute and an inline
      // `width: Npx` style so resized images render correctly in real email
      // clients (Gmail, Outlook, Apple Mail) — not just the editor. See
      // image-resize-extension.ts for the full email-client compat rationale.
      //
      // inline: false (block image) so text after an image renders below it,
      // matching the sent email. With inline: true the resize NodeView wraps
      // the image in `float: left; display: inline-block`, floating following
      // text to its RIGHT in the editor only (the float is stripped from the
      // serialized HTML, so the send diverges from the compose surface).
      createImageExtension({
        inline: false,
        allowBase64: true,
        htmlClass: 'max-w-full h-auto rounded',
      }),
    ],
    content: defaultBody,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px]',
      },
      ...linkEditorProps(),
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageInsert(file);
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;

        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length > 0) {
          event.preventDefault();
          imageFiles.forEach(handleImageInsert);
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && defaultBody) {
      editor.commands.setContent(defaultBody);
    }
  }, [editor, defaultBody]);

  useEffect(() => {
    setCharacterCount(subject.length);
  }, [subject]);

  const applyRaw = (tpl: { subject: string; body: string }) => {
    setSubject(tpl.subject);
    editor?.commands.setContent(tpl.body);
    // Raw inserts are not personalized — drop any stale personalization marker
    // and recipient-mismatch caution.
    setPopulatedFrom(null);
    setRecipientChangedAfterPopulate(null);
    // A raw insert leaves no gaps the no-contact flag is meant to warn about.
    // The no-contact route re-sets the flag explicitly after calling applyRaw.
    setShowNoContactFlag(false);
  };

  // Wrap recipient changes so we can surface a caution when the recipient is
  // swapped after the body was personalized for someone else (FIX 1).
  const handleRecipientChange = (next: ToRecipient) => {
    userTouchedTo.current = true;
    setTo(next);
    if (!populatedFrom) {
      setRecipientChangedAfterPopulate(null);
      return;
    }
    const stillSameContact = next?.kind === 'contact' && next.contact.id === populatedFrom.id;
    setRecipientChangedAfterPopulate(stillSameContact ? null : populatedFrom.name);
  };

  const handleTemplateChosen = async (tpl: { subject: string; body: string }) => {
    const route = decideTemplateRoute(tpl, to);
    if (route === 'raw') { applyRaw(tpl); return; }
    if (route === 'populate') { setPendingTemplate(tpl); return; } // opens dialog
    // no-contact: warn-before-insert
    const ok = await confirm({
      title: 'Insert template without filling?',
      description: "You're emailing an address that isn't a saved contact, so this template's personalized fields (like the name) can't be filled in automatically. Insert it anyway?",
      confirmLabel: 'Insert anyway',
      cancelLabel: 'Cancel',
    });
    if (ok) {
      applyRaw(tpl);
      setShowNoContactFlag(true);
    }
  };

  const handleSend = async () => {
    if (blockIfEmulating('Sending an email on the user\'s behalf')) return;
    const toEmail = to ? (to.kind === 'contact' ? (to.contact.primary_email ?? '') : to.email) : '';
    if (!selectedProvider || !toEmail.trim() || !editor) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Check attachment size limits
    const totalSize = attachments.reduce((sum, att) => sum + att.size, 0);
    if (selectedProvider.type === 'gmail' && totalSize > GMAIL_MAX_SIZE) {
      toast({
        title: "Error",
        description: `Total attachment size (${formatBytes(totalSize)}) exceeds Gmail's 25MB limit`,
        variant: "destructive",
      });
      return;
    }

    if (sending) return;

    const warning = buildUnfilledWarning(subject, editor.getText(), editor.getHTML());
    if (warning && !(await confirm({ title: "Some blanks aren't filled in", description: warning, confirmLabel: 'Send anyway', cancelLabel: 'Go back' }))) {
      track('oneoff_send_gate_blocked', { source: 'oneoff_composer', workspace_id: currentWorkspace?.id });
      return;
    }

    // Stale-PII backstop: body was personalized for one contact but the
    // recipient was changed to someone else.
    const currentMatchesPopulatedContact =
      to?.kind === 'contact' && populatedFrom?.id === to.contact.id;
    const mismatchWarning = buildRecipientMismatchWarning(
      populatedFrom?.name ?? null,
      currentMatchesPopulatedContact,
    );
    if (mismatchWarning && !(await confirm({ title: 'Recipient changed', description: mismatchWarning, confirmLabel: 'Send anyway', cancelLabel: 'Go back' }))) {
      track('oneoff_recipient_mismatch_blocked', { source: 'oneoff_composer', workspace_id: currentWorkspace?.id });
      return;
    }

    setSending(true);
    try {
      await sendEmail({
        providerId: selectedProvider.id,
        to: [toEmail],
        cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: subject.trim(),
        body: editor.getHTML(),
        signature: selectedProvider.signature,
        attachments: attachments.length > 0 ? attachments : undefined,
        contactId: to?.kind === 'contact' ? to.contact.id : undefined,
      });

      toast({
        description: "Email sent successfully",
      });

      onClose?.();
    } catch (error) {
      console.error('Error sending email:', error);
      if (!showReconnectErrorToast(error, selectedProvider?.type)) {
        captureError(error as Error, { feature: 'email-composer-send' });
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to send email. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setSending(false);
    }
  };

  const handleSave = () => {
    if (!editor || !onSave) return;
    onSave(subject, editor.getHTML());
  };

  const handleAttachFiles = () => {
    fileInputRef.current?.click();
  };

  const handleInsertImage = () => {
    imageInputRef.current?.click();
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(handleImageInsert);
    }
    event.target.value = '';
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newAttachments: EmailAttachment[] = Array.from(files).map(file => ({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  // Calculate total attachment size
  const totalAttachmentSize = attachments.reduce((sum, att) => sum + att.size, 0);
  const isOverSizeLimit = totalAttachmentSize > GMAIL_MAX_SIZE;
  const isNearSizeLimit = totalAttachmentSize > GMAIL_WARNING_SIZE;

  const removeAttachment = (id: string) => {
    setAttachments(attachments.filter(att => att.id !== id));
  };

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-0 right-4 flex items-center bg-card border rounded-t-lg shadow-lg cursor-pointer z-50" 
        onClick={onMinimize}
      >
        <div className="px-4 py-2 flex items-center justify-between w-[300px]">
          <span className="font-medium truncate flex-1">
            {subject || "New Message"}
          </span>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.stopPropagation();
                onMinimize?.();
              }}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={composerRef}
      className={cn(
        "fixed bottom-0 right-4 w-[700px] bg-card border rounded-t-lg shadow-lg flex flex-col",
        "z-50 transition-all duration-200 ease-in-out",
        isMinimized ? "h-[40px]" : "h-[600px]"
      )}
      style={{ maxHeight: "calc(100vh - 60px)" }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
        <h3 className="font-semibold text-lg">New Message</h3>
        <div className="flex items-center gap-1">
          {onMinimize && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onMinimize}>
              <Minimize2 className="h-4 w-4" />
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Email Form */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-4 space-y-4 border-b bg-card">
          {/* From Field */}
          <div className="flex items-center gap-2">
            <span className="w-16 text-sm font-medium">From:</span>
            <div className="flex-1">
              <SenderSelect
                value={selectedProvider?.id}
                onValueChange={selectProvider}
                disabled={providerLoading}
              />
            </div>
          </div>

          {/* No Sender Profile Message - Only show after loading and when no providers exist */}
          {initialized && !providerLoading && providers.length === 0 && (
            <div className="text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 p-2 rounded border border-amber-200 dark:border-amber-800">
              <Link to="/settings/senders" className="flex items-center gap-2 hover:underline">
                <Mail className="h-4 w-4" />
                <span>No sender profile set up. Click here to set up sender profile.</span>
              </Link>
            </div>
          )}

          {/* Recipients */}
          <div className="flex items-center gap-2">
            <span className="w-16 text-sm font-medium">To:</span>
            <div className="flex-1">
              <ToContactPicker value={to} onChange={handleRecipientChange} />
            </div>
            <div className="flex items-center gap-2">
              <Toggle
                pressed={showCc}
                onPressedChange={setShowCc}
                variant="outline"
                size="sm"
              >
                Cc
              </Toggle>
              <Toggle
                pressed={showBcc}
                onPressedChange={setShowBcc}
                variant="outline"
                size="sm"
              >
                Bcc
              </Toggle>
            </div>
          </div>

          {showNoContactFlag && (
            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>This email has blanks that weren’t filled in (no contact selected). Edit them before sending.</span>
            </div>
          )}

          {recipientChangedAfterPopulate && (
            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Recipient changed after personalizing for {recipientChangedAfterPopulate} — review the email before sending.
              </span>
            </div>
          )}

          {showCc && (
            <div className="flex items-center gap-2">
              <span className="w-16 text-sm font-medium">Cc:</span>
              <Input
                type="email"
                placeholder="Cc recipients"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className="border-input/60 focus:border-primary"
              />
            </div>
          )}

          {showBcc && (
            <div className="flex items-center gap-2">
              <span className="w-16 text-sm font-medium">Bcc:</span>
              <Input
                type="email"
                placeholder="Bcc recipients"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                className="border-input/60 focus:border-primary"
              />
            </div>
          )}

          {/* Subject */}
          <div>
            <div className="flex items-center gap-2">
              <span className="w-16 text-sm font-medium">Subject:</span>
              <div className="flex-1 relative">
                <Input
                  placeholder="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={100}
                  className="border-input/60 focus:border-primary pr-16"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                  {characterCount}/100
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Formatting Toolbar */}
        <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-1">
            {/* Text Styling Group */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={cn("h-8 w-8 p-0", editor?.isActive('bold') && "bg-muted")}
              aria-label="Bold"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={cn("h-8 w-8 p-0", editor?.isActive('italic') && "bg-muted")}
              aria-label="Italic"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              className={cn("h-8 w-8 p-0", editor?.isActive('underline') && "bg-muted")}
              aria-label="Underline"
            >
              <Underline className="h-4 w-4" />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-6" />

            {/* Heading Group */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
              className={cn("h-8 px-2", editor?.isActive('heading', { level: 1 }) && "bg-muted")}
              aria-label="Heading 1"
            >
              <Heading1 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn("h-8 px-2", editor?.isActive('heading', { level: 2 }) && "bg-muted")}
              aria-label="Heading 2"
            >
              <Heading2 className="h-4 w-4" />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-6" />

            {/* List Group */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              className={cn("h-8 w-8 p-0", editor?.isActive('bulletList') && "bg-muted")}
              aria-label="Bullet List"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              className={cn("h-8 w-8 p-0", editor?.isActive('orderedList') && "bg-muted")}
              aria-label="Ordered List"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-6" />

            {/* Link */}
            <LinkPopover editor={editor} />

            {/* Image */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleInsertImage}
              className="h-8 w-8 p-0"
              aria-label="Insert image"
              title="Insert image"
            >
              <ImageIcon className="h-4 w-4" />
            </Button>

            <Separator orientation="vertical" className="mx-1 h-6" />

            {/* Template Selector */}
            <TemplateSelector
              density="composer"
              onSelect={handleTemplateChosen}
            />
          </div>
        </div>

        {/* Editor — internal scroll so pasting a long email doesn't grow the
            composer beyond the viewport. max-h caps the editor area. min-h-0
            lets it flex-shrink so the attachments block + footer stay on-screen
            (the inner EditorContent keeps its own min-h-[200px] typing surface,
            which scrolls when the editor area is squeezed). */}
        <div className="flex-1 min-h-0 max-h-[50vh] overflow-y-auto p-4">
          <EditorContent editor={editor} className="h-full" />
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="p-4 border-t bg-muted/20">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Attachments</h4>
              <span className={cn(
                "text-xs",
                isOverSizeLimit ? "text-destructive font-medium" :
                isNearSizeLimit ? "text-yellow-600 dark:text-yellow-500" :
                "text-muted-foreground"
              )}>
                {formatBytes(totalAttachmentSize)} / 25 MB
              </span>
            </div>
            {isOverSizeLimit && (
              <p className="text-xs text-destructive mb-2">
                Total size exceeds Gmail's 25MB limit. Remove some files to send.
              </p>
            )}
            {isNearSizeLimit && !isOverSizeLimit && (
              <p className="text-xs text-yellow-600 dark:text-yellow-500 mb-2">
                Approaching size limit. Consider removing large files.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {attachments.map((att) => (
                <Badge
                  key={att.id}
                  variant="secondary"
                  className="flex items-center gap-1 py-1"
                >
                  <span className="truncate max-w-[150px]">{att.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatBytes(att.size)})
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    onClick={() => removeAttachment(att.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t flex items-center gap-2 bg-muted/30">
          <Button
            onClick={handleSend}
            disabled={!selectedProvider || !to || sending || isOverSizeLimit || isEmulating}
            title={isEmulating ? 'Disabled while emulating user' : undefined}
            className="gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send
              </>
            )}
          </Button>
          {onSave && (
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={!editor}
            >
              Save Draft
            </Button>
          )}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            onChange={handleFileChange}
          />
          <input
            type="file"
            ref={imageInputRef}
            className="hidden"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            onChange={handleImageFileChange}
          />
          <Button variant="outline" onClick={handleAttachFiles}>
            <Paperclip className="h-4 w-4 mr-2" />
            Attach
          </Button>
        </div>
      </div>

      <TemplatePopulateDialog
        open={!!pendingTemplate}
        template={pendingTemplate}
        contact={to?.kind === 'contact' ? to.contact : null}
        onPopulate={(r: PopulateResult) => {
          setSubject(r.subject);
          editor?.commands.setContent(r.body);
          setPendingTemplate(null);
          setShowNoContactFlag(false);
          // Record which contact we personalized from (FIX 1 backstop). The
          // populate route only runs when `to` is a contact.
          if (to?.kind === 'contact') {
            const c = to.contact;
            const displayName =
              [c.first_name, c.last_name].filter(Boolean).join(' ').trim() ||
              c.primary_email ||
              'this contact';
            setPopulatedFrom({ id: c.id ?? '', name: displayName });
          }
          // Body now matches the current recipient again.
          setRecipientChangedAfterPopulate(null);
          track('oneoff_template_populated', { source: 'oneoff_composer', workspace_id: currentWorkspace?.id });
        }}
        onInsertRaw={() => {
          if (pendingTemplate) applyRaw(pendingTemplate);
          setPendingTemplate(null);
        }}
        onCancel={() => setPendingTemplate(null)}
      />
      {ConfirmDialog}
    </div>
  );
}